import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ReactiveFormsModule, UntypedFormBuilder, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {Location as AngularLocation} from '@angular/common';
import {MatSnackBar} from '@angular/material/snack-bar';
import {Observable, Subscription} from 'rxjs';
import {Account} from '../../models/account';
import {ProgressComponent} from '../progress/progress.component';
import {tap} from 'rxjs/operators';
import {BreadCrumb} from '../../models/breadcrumb';
import {DialogService} from '../../services/dialog.service';
import {CanComponentDeactivate} from '../../guards/can-deactive.guard';
import {
    addDoc,
    collection,
    doc,
    docSnapshots,
    DocumentData,
    DocumentReference,
    Firestore,
    serverTimestamp,
    updateDoc
} from '@angular/fire/firestore';
import {BreadcrumbsComponent} from '../breadcrumbs/breadcrumbs.component';
import {MatCard, MatCardContent, MatCardHeader, MatCardTitle} from '@angular/material/card';
import {MatError, MatInput} from '@angular/material/input';
import {MatButton} from '@angular/material/button';
import {MatFormField} from '@angular/material/form-field';

@Component({
    selector: 'app-account-form',
    templateUrl: './account-form.component.html',
    styleUrls: ['./account-form.component.css'],
    imports: [
        BreadcrumbsComponent,
        ProgressComponent,
        MatCard,
        MatCardTitle,
        MatCardHeader,
        MatCardContent,
        ReactiveFormsModule,
        MatFormField,
        MatInput,
        MatError,
        MatButton
    ],
    standalone: true
})
export class AccountFormComponent implements OnInit, OnDestroy, CanComponentDeactivate {

    @ViewChild(ProgressComponent, {static: true}) progress: ProgressComponent;

    account: Observable<DocumentData>;
    accountId: string;
    subscription: Subscription;
    breadcrumbs: BreadCrumb[] = [
        {url: ['/'], title: 'Accounts'},
        {url: null, title: 'New Account'}
    ];
    accountForm = this.formBuilder.group({
        name: ['', Validators.required],
    });
    private accountDocument: DocumentReference;

    constructor(
        private formBuilder: UntypedFormBuilder,
        private firestore: Firestore,
        private router: Router,
        private activeRoute: ActivatedRoute,
        private angularLocation: AngularLocation,
        private snackBar: MatSnackBar,
        private dialogService: DialogService) {
    }

    ngOnInit() {
        this.activeRoute.paramMap.subscribe(
            params => {
                this.accountId = params.get('accountId');
                if (this.accountId) {
                    this.fetchAccount();
                }
            }
        );
    }

    canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
        if (this.accountForm.dirty) {
            const confirm: Observable<boolean> = this.dialogService.confirm('Discard changes for Account?');
            confirm.subscribe(conf => {
                if (!conf) {
                    this.angularLocation.go(this.router.url);
                    return false;
                }
            });
            return confirm;
        }
        return true;
    }

    fetchAccount() {
        this.progress.setVisible(true);
        this.accountDocument = doc(collection(this.firestore, 'accounts'), this.accountId);
        this.account = docSnapshots(this.accountDocument);
        this.subscription = this.account.pipe(
            tap(data => {
                this.accountForm.patchValue(data);
                this.breadcrumbs[1].title = `Edit: ${data.name}`;
                this.progress.setVisible(false);
            })
        ).subscribe();
    }

    async submit() {
        this.progress.setVisible(true);
        if (this.accountId) {
            await updateDoc(this.accountDocument, this.accountForm.value);
        } else {
            const model = {} as Account;
            model.created = serverTimestamp();
            await addDoc(collection(this.firestore, 'accounts'), {...model, ...this.accountForm.value});
        }
        this.progress.setVisible(false);
        this.snackBar.open(this.accountForm.value.name + ' account ' + (this.accountId ? 'updated' : 'saved'), null, {duration: 3000});
        this.accountForm.markAsPristine();
        this.angularLocation.back();
    }

    ngOnDestroy(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }
}
